import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import SubHeader from "components/common/SubHeader";
import MenuList from "components/mypage/MenuList";
import ProfileAvatar from "components/common/ProfileAvatar";
import { PB_DISCIPLINES, getBestPb } from "api/freedivingPb";
import { getLicenseList, updateUserData } from "api/user";
import { getMyInstructorProfile } from "api/instructor";
import { useSnackbar } from "contexts/SnackbarContext";
import PbInputSheet from "components/profile/PbInputSheet";
import { getPublicUserKey } from "utils/userIdentity";

const InlineToggle = ({ checked, onChange, label = "공개여부" }) => (
    <label className="inline-visibility-toggle">
        <span className="inline-visibility-toggle__label">{label}</span>
        <span className="inline-visibility-toggle__switch">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="inline-visibility-toggle__slider" />
        </span>
    </label>
);

const User = () => {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const { showError } = useSnackbar();

    const [pbRecords, setPbRecords] = useState([]);
    const [licenseList, setLicenseList] = useState([]);
    const [pbVisible, setPbVisible] = useState(userInfo.pb_visible ?? true);
    const [certVisible, setCertVisible] = useState(userInfo.cert_visible ?? true);
    const [showPbSheet, setShowPbSheet] = useState(false);
    const [instructorProfile, setInstructorProfile] = useState(null);
    const isInstructor = !!userInfo.is_staff;

    useEffect(() => {
        getBestPb().then(setPbRecords);
        getLicenseList().then((res) => {
            if (Array.isArray(res)) setLicenseList(res);
        });
    }, []);

    useEffect(() => {
        if (!isInstructor) return;
        getMyInstructorProfile().then((res) => {
            if (res) setInstructorProfile(res);
        });
    }, [isInstructor]);

    useEffect(() => {
        setPbVisible(userInfo.pb_visible ?? true);
        setCertVisible(userInfo.cert_visible ?? true);
    }, [userInfo.pb_visible, userInfo.cert_visible]);

    const handleToggle = async (key, next) => {
        const prev = key === "pb_visible" ? pbVisible : certVisible;
        if (key === "pb_visible") setPbVisible(next);
        else setCertVisible(next);
        try {
            const res = await updateUserData({ [key]: next });
            if (res?.code !== "0000") throw new Error("update failed");
            setUserInfo({ ...userInfo, [key]: next });
        } catch {
            if (key === "pb_visible") setPbVisible(prev);
            else setCertVisible(prev);
            showError("공개 설정 변경에 실패했습니다");
        }
    };

    const pbByCode = new Map(pbRecords.map((r) => [r.discipline, r]));
    const pbItems = PB_DISCIPLINES.map((d) => {
        const rec = pbByCode.get(d.code);
        const unitSuffix = d.type === "seconds" ? "mm:ss" : "m";
        return {
            code: d.code,
            title: d.label,
            description: rec?.value_display || `- ${unitSuffix}`,
        };
    });

    const approvedLicenses = licenseList.filter((l) => l.is_approved);

    return (
        <div className="page-user-profile">
            <SubHeader title="내 정보 관리" />
            <ProfileAvatar
                imageUrl={userInfo.profile_image_url}
                size={100}
                className="user-profile-photo"
            />
            <div className="profile-contents">
                <MenuList
                    title="👤 프로필"
                    btn={{
                        name: (
                            <>
                                편집 <svg
                                    className="chevron-icon"
                                    width="6"
                                    height="10"
                                    viewBox="0 0 6 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M1 1L5 5L1 9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </>
                        ),
                        link: "/mypage/user/edit?info=user",
                    }}
                    list={[
                        { title: "이메일", description: userInfo.user_email, link: null },
                        { title: "연락처", description: userInfo.hp, link: null },
                        { title: "프로필명", description: userInfo.nickname, link: null },
                        { title: "소개", description: userInfo.bio || "-", link: null },
                    ]}
                />
                <div className="divider"></div>

                {isInstructor && instructorProfile && (
                    <>
                        <div className="menu-list-wrap menu-list-wrap--instructor">
                            <p className="menu-title bold">
                                <span>🧑‍🏫 강사 프로필</span>
                                <span
                                    className="menu-btn"
                                    onClick={() =>
                                        navigate("/mypage/instructor/edit")
                                    }
                                >
                                    편집 <svg
                                        className="chevron-icon"
                                        width="6"
                                        height="10"
                                        viewBox="0 0 6 10"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M1 1L5 5L1 9"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </p>
                            <div>
                                <div className="menu-item">
                                    <p className="menu-name">
                                        <span>이메일</span>
                                        <span className="menu-sub menu-sub--right">
                                            {instructorProfile.contact_email || "-"}
                                        </span>
                                    </p>
                                </div>
                                <div className="menu-item">
                                    <p className="menu-name">
                                        <span>연락처</span>
                                        <span className="menu-sub menu-sub--right">
                                            {instructorProfile.contact_phone || "-"}
                                        </span>
                                    </p>
                                </div>
                                <div className="menu-item">
                                    <p className="menu-name">
                                        <span>종목</span>
                                        <span className="menu-sub menu-sub--right">
                                            {instructorProfile.sports?.length
                                                ? instructorProfile.sports
                                                      .map((s) => s.name)
                                                      .join(", ")
                                                : "-"}
                                        </span>
                                    </p>
                                </div>
                                <div className="menu-item">
                                    <p className="menu-name">
                                        <span>관심사</span>
                                        <span className="menu-sub menu-sub--right">
                                            {instructorProfile.interests || "-"}
                                        </span>
                                    </p>
                                </div>
                                <div className="menu-item menu-item--block">
                                    <p className="menu-name">
                                        <span>강사 소개글</span>
                                    </p>
                                    <p className="instructor-bio">
                                        {instructorProfile.introduction || "-"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="divider"></div>
                    </>
                )}

                <div className="menu-list-wrap menu-list-wrap--grid2">
                    <p className="menu-title bold">
                        <span>📊 종목별 최고 기록</span>
                        <InlineToggle
                            checked={pbVisible}
                            onChange={(e) => handleToggle("pb_visible", e.target.checked)}
                        />
                    </p>
                    <p className="pb-register-desc pb-register-desc--top">
                        {isInstructor
                            ? "강사는 직접 등록할 수 있어요"
                            : "강습·연습반 이후 강사님이 등록해 줄 예정이에요"}
                    </p>
                    <div>
                        {pbItems.map((it) => (
                            <div key={it.code} className="menu-item">
                                <p className="menu-name">
                                    <span>{it.title}</span>
                                    <span style={{ marginLeft: "3px" }} className="menu-sub">
                                        {it.description}
                                    </span>
                                </p>
                            </div>
                        ))}
                    </div>
                    {getPublicUserKey(userInfo) && (
                        <button
                            type="button"
                            className="pb-history-link"
                            onClick={() =>
                                navigate(`/pb/history/${encodeURIComponent(getPublicUserKey(userInfo))}`)
                            }
                        >
                            상세 이력 보기
                        </button>
                    )}
                    {isInstructor && (
                        <button
                            type="button"
                            className="btn btn-purple500 btn-large pb-register-btn"
                            onClick={() => setShowPbSheet(true)}
                        >
                            PB 등록
                        </button>
                    )}
                </div>
                <div className="divider"></div>

                <div className="menu-list-wrap menu-list-wrap--certs">
                    <p className="menu-title bold">
                        <span>🎓 내 자격증</span>
                        <InlineToggle
                            checked={certVisible}
                            onChange={(e) => handleToggle("cert_visible", e.target.checked)}
                        />
                    </p>
                    <div>
                        {approvedLicenses.length === 0 ? (
                            <p className="empty-placeholder">등록된 자격증이 없습니다</p>
                        ) : (
                            approvedLicenses.map((l) => (
                                <div key={l.id} className="menu-item">
                                    <p className="menu-name">
                                        <span>{l.certificate_name}</span>
                                    </p>
                                    {l.uploaded_at && (
                                        <span className="menu-sub menu-sub--right">
                                            {l.uploaded_at.split("T")[0].replaceAll("-", ".")} 승인일
                                        </span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-purple500 btn-large pb-register-btn"
                        onClick={() =>
                            navigate(`/verify/${isInstructor ? "instructor" : "student"}`)
                        }
                    >
                        자격증 등록
                    </button>
                </div>
                <div className="divider"></div>

                <PbInputSheet
                    isOpen={showPbSheet}
                    onClose={() => setShowPbSheet(false)}
                    userId={userInfo.user_id}
                    historyUserKey={getPublicUserKey(userInfo)}
                    userName={userInfo.nickname || userInfo.name}
                    onSaved={() => getBestPb().then(setPbRecords)}
                />

            </div>
        </div>
    );
};

export default User;

//다른 페이지도 사용하면 컴포넌트 분리
export const UserInfo = ({ className, title, rowData }) => {
    const navigate = useNavigate();
    return (
        <div className={`info-contents-wrap ${className}`}>
            <div className={`user-info-title`}>
                {title && <p>{title}</p>}
                {title === "계정" && (
                    <span
                        onClick={() => navigate("/mybuccl/user/edit?info=user")}
                    >{`수정 >`}</span>
                )}
            </div>
            <div className="user-info-table">
                {rowData.map((row, idx) => (
                    <>
                        <div className="user-info-table-row">
                            <div
                                className={`label ${
                                    !!title ? "" : "account-label"
                                }`}
                            >
                                {row.label}
                            </div>
                            <div
                                className={`value ${
                                    !!title ? "" : "account-label"
                                }`}
                            >
                                {row.value}
                                {row.editable && " >"}
                            </div>
                        </div>
                        {idx !== rowData.length - 1 && (
                            <div className="user-info-divider"></div>
                        )}
                    </>
                ))}
            </div>
        </div>
    );
};
